/**
 * Modul Eveniment pe client / tabletă / slug public.
 */
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';
import { getPublicClientSlug } from '../services/public-client.js?v=83.21';
import { submitEventMessageBackend } from '../services/backend.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import {
  DEFAULT_EVENT_BUTTON_LABEL,
  DEFAULT_EVENT_TITLE
} from './event-module-admin.js?v=83.21';
import { isCustomTemplateActive } from './client-template-style.js?v=83.21';

let detachOverlayListeners = null;
let overlayKeyHandler = null;
let isSubmittingEventMessage = false;

function buildEventImages(settings) {
  const em = settings?.eventModule;
  if (!em || em.enabled !== true || !Array.isArray(em.images)) return [];
  return em.images.map((u) => String(u || '').trim()).filter(Boolean).slice(0, 10);
}

function isEventModuleActive(settings) {
  return settings?.eventModule?.enabled === true;
}

/** Vizibil pe client — un singur modul principal activ (campanie / fidelizare / evenimente). */
export function isEventVisible(settings) {
  if (!isEventModuleActive(settings)) return false;
  if ((settings?.campaignStatus || 'draft') === 'active') return false;
  if (settings?.loyaltySettings?.active === true) return false;
  // După ziua evenimentului, butonul dispare singur.
  const d = String(settings?.eventModule?.eventDate || '').trim();
  if (d) {
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date());
    if (d < today) return false;
  }
  return true;
}

/** „Sâmbătă, 18 octombrie · 20:00” */
export function formatEventWhen(em) {
  const d = String(em?.eventDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return '';
  const [y, m, day] = d.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, day, 12));
  let label = '';
  try {
    label = new Intl.DateTimeFormat('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(dt);
    label = label.charAt(0).toUpperCase() + label.slice(1);
  } catch (_) {
    label = d.split('-').reverse().join('.');
  }
  const t = String(em?.eventTime || '').trim();
  return /^\d{2}:\d{2}$/.test(t) ? `${label} · ${t}` : label;
}

function getEventCtaTrigger() {
  return document.getElementById('playBtn') || document.getElementById('clientEventModuleTrigger');
}

function closeOverlayShell(overlay, trigger) {
  if (!overlay) return;
  overlay.setAttribute('hidden', '');
  overlay.classList.remove('is-open');
  overlay.setAttribute('aria-hidden', 'true');
  const cta = getEventCtaTrigger();
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
  if (cta && cta !== trigger) cta.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('event-module-open');
  resetMessageForm();
}

function resetMessageForm() {
  const form = document.getElementById('eventModuleMessageForm');
  const input = document.getElementById('eventModuleMessageInput');
  const success = document.getElementById('eventModuleMessageSuccess');
  const showBtn = document.getElementById('eventModuleShowMessageBtn');
  if (form) form.hidden = true;
  if (input) input.value = '';
  if (success) success.hidden = true;
  if (showBtn) showBtn.style.display = '';
}

function resolveEventMessageSource() {
  const slug = getPublicClientSlug();
  if (slug) return 'public-client';
  const menuMode = document.getElementById('clientMenuMode');
  if (menuMode && !menuMode.hidden) return 'tablet';
  return 'client';
}

export function openEventModuleOverlay() {
  const overlay = document.getElementById('eventModuleOverlay');
  const trigger = getEventCtaTrigger();
  if (!overlay) return;
  overlay.removeAttribute('hidden');
  overlay.classList.add('is-open');
  overlay.setAttribute('aria-hidden', 'false');
  if (trigger) trigger.setAttribute('aria-expanded', 'true');
  document.body.classList.add('event-module-open');
  resetMessageForm();
}

export function closeEventModuleOverlay() {
  const overlay = document.getElementById('eventModuleOverlay');
  closeOverlayShell(overlay, getEventCtaTrigger());
}

export async function toggleEventMessageForm() {
  const form = document.getElementById('eventModuleMessageForm');
  const showBtn = document.getElementById('eventModuleShowMessageBtn');
  if (!form) return;
  const willShow = form.hidden;
  form.hidden = !willShow;
  if (showBtn) showBtn.style.display = willShow ? 'none' : '';
  if (willShow) {
    document.getElementById('eventModuleMessageInput')?.focus();
  }
}

export async function submitEventMessage() {
  if (isSubmittingEventMessage) return;
  const input = document.getElementById('eventModuleMessageInput');
  const success = document.getElementById('eventModuleMessageSuccess');
  const text = String(input?.value || '').trim();
  if (text.length < 2) {
    showSnackbar('Mesajul trebuie să aibă cel puțin 2 caractere.', 'error');
    return;
  }
  if (text.length > 500) {
    showSnackbar('Mesajul poate avea maxim 500 de caractere.', 'error');
    return;
  }

  isSubmittingEventMessage = true;
  try {
    await submitEventMessageBackend({
      text,
      publicSlug: getPublicClientSlug() || null,
      source: resolveEventMessageSource()
    });
    if (input) input.value = '';
    const form = document.getElementById('eventModuleMessageForm');
    if (form) form.hidden = true;
    if (success) success.hidden = false;
    const showBtn = document.getElementById('eventModuleShowMessageBtn');
    if (showBtn) showBtn.style.display = 'none';
  } catch (e) {
    showSnackbar(e?.message || 'Nu am putut trimite mesajul.', 'error');
  } finally {
    isSubmittingEventMessage = false;
  }
}

export function applyEventModuleUI(settings) {
  if (isCustomTemplateActive()) return;

  if (detachOverlayListeners) {
    try {
      detachOverlayListeners();
    } catch (_) {
      /* ignore */
    }
    detachOverlayListeners = null;
  }
  if (overlayKeyHandler) {
    document.removeEventListener('keydown', overlayKeyHandler);
    overlayKeyHandler = null;
  }

  const wrap = document.getElementById('layoutBlockEvent');
  const trigger = document.getElementById('clientEventModuleTrigger');
  const triggerLabel = document.getElementById('clientEventModuleTriggerLabel');
  const menuBtn = document.getElementById('clientMenuEventModuleBtn');
  const overlay = document.getElementById('eventModuleOverlay');
  const overlayTitle = document.getElementById('eventModuleOverlayTitle');
  const overlayText = document.getElementById('eventModuleOverlayText');
  const overlayViewport = document.getElementById('eventModuleOverlayViewport');
  const overlayTrack = document.getElementById('eventModuleOverlayTrack');
  const overlayDots = document.getElementById('eventModuleOverlayDots');
  const closeBtn = document.getElementById('eventModuleOverlayClose');
  const backdrop = document.getElementById('eventModuleOverlayBackdrop');

  const active = isEventVisible(settings);
  const buttonLabel =
    String(settings?.eventModule?.buttonLabel || '').trim() || DEFAULT_EVENT_BUTTON_LABEL;
  const eventTitle =
    String(settings?.eventModule?.title || '').trim() || DEFAULT_EVENT_TITLE;
  const eventText = String(settings?.eventModule?.text || '').trim();

  if (wrap) {
    wrap.style.display = 'none';
    wrap.setAttribute('aria-hidden', 'true');
  }
  if (trigger) trigger.style.display = 'none';
  if (triggerLabel) triggerLabel.textContent = buttonLabel;
  if (menuBtn) {
    menuBtn.style.display = active ? 'inline-flex' : 'none';
    menuBtn.setAttribute('aria-label', buttonLabel);
  }

  if (!active) {
    closeOverlayShell(overlay, trigger);
    import('./client-primary-module.js?v=83.21')
      .then((m) => m.syncPrimaryModuleClientUI?.(settings))
      .catch(() => {});
    return;
  }

  if (
    !overlay ||
    !overlayTitle ||
    !overlayText ||
    !overlayViewport ||
    !overlayTrack ||
    !overlayDots ||
    !closeBtn ||
    !backdrop
  ) {
    return;
  }

  closeOverlayShell(overlay, trigger);
  overlayTrack.replaceChildren();
  overlayDots.replaceChildren();
  overlayTitle.textContent = eventTitle;
  overlayText.textContent = eventText;
  overlayText.style.display = eventText ? 'block' : 'none';
  let whenEl = document.getElementById('eventModuleOverlayWhen');
  const when = formatEventWhen(settings?.eventModule);
  if (!whenEl && overlayTitle) {
    whenEl = document.createElement('p');
    whenEl.id = 'eventModuleOverlayWhen';
    whenEl.className = 'event-module-overlay__when';
    overlayTitle.insertAdjacentElement('afterend', whenEl);
  }
  if (whenEl) {
    whenEl.textContent = when ? `📅 ${when}` : '';
    whenEl.hidden = !when;
  }

  const images = buildEventImages(settings);
  images.forEach((url) => {
    const safe = sanitizeImageSrc(url);
    if (!safe) return;
    const slide = document.createElement('div');
    slide.className = 'event-module__slide';
    const img = document.createElement('img');
    img.className = 'event-module__img';
    img.alt = '';
    img.decoding = 'async';
    img.loading = 'lazy';
    img.onload = () => img.classList.add('img-loaded');
    img.onerror = () => img.classList.add('img-failed');
    img.src = safe;
    slide.appendChild(img);
    overlayTrack.appendChild(slide);
  });

  const carouselWrap = document.getElementById('eventModuleCarouselWrap');
  const n = overlayTrack.children.length;
  if (carouselWrap) {
    carouselWrap.style.display = n > 0 ? 'block' : 'none';
  }

  const carouselCleanups = [];

  if (n === 0) {
    overlayViewport.style.display = 'none';
  } else {
    overlayViewport.style.display = '';
    const pct = 100 / n;
    overlayTrack.style.width = `${n * 100}%`;
    Array.from(overlayTrack.children).forEach((slide) => {
      slide.style.flex = `0 0 ${pct}%`;
    });

    const dotEls = [];
    if (n > 1) {
      for (let i = 0; i < n; i++) {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'event-module__dot';
        d.setAttribute('aria-label', `Imagine ${i + 1} din ${n}`);
        overlayDots.appendChild(d);
        dotEls.push(d);
      }
    }

    let index = 0;
    function setTransform() {
      overlayTrack.style.transform = `translateX(calc(-100% * ${index} / ${n}))`;
      dotEls.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }
    setTransform();

    if (n > 1) {
      let startX = 0;
      let dragging = false;

      const onPointerDown = (e) => {
        dragging = true;
        startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
        overlayTrack.style.transition = 'none';
      };
      const onPointerUp = (e) => {
        if (!dragging) return;
        dragging = false;
        overlayTrack.style.transition = '';
        const x = e.clientX ?? e.changedTouches?.[0]?.clientX ?? startX;
        const dx = x - startX;
        const threshold = 48;
        if (dx < -threshold && index < n - 1) index++;
        else if (dx > threshold && index > 0) index--;
        setTransform();
      };

      overlayViewport.addEventListener('pointerdown', onPointerDown, { passive: true });
      overlayViewport.addEventListener('pointerup', onPointerUp);
      overlayViewport.addEventListener('pointercancel', onPointerUp);
      carouselCleanups.push(() => {
        overlayViewport.removeEventListener('pointerdown', onPointerDown);
        overlayViewport.removeEventListener('pointerup', onPointerUp);
        overlayViewport.removeEventListener('pointercancel', onPointerUp);
      });

      dotEls.forEach((d, i) => {
        const onDot = () => {
          index = i;
          setTransform();
        };
        d.addEventListener('click', onDot);
        carouselCleanups.push(() => d.removeEventListener('click', onDot));
      });
    }
  }

  const onTriggerClick = (e) => {
    e.preventDefault();
    import('../services/client-analytics.js?v=83.21')
      .then((m) => m.trackClientEvent('event', 'open'))
      .catch(() => {});
    openEventModuleOverlay();
  };
  const onMenuClick = (e) => {
    e.preventDefault();
    openEventModuleOverlay();
  };
  const onCloseClick = () => closeEventModuleOverlay();
  const onBackdropClick = (ev) => {
    if (ev.target === backdrop) closeEventModuleOverlay();
  };

  if (trigger) trigger.addEventListener('click', onTriggerClick);
  if (menuBtn) menuBtn.addEventListener('click', onMenuClick);
  closeBtn.addEventListener('click', onCloseClick);
  backdrop.addEventListener('click', onBackdropClick);

  overlayKeyHandler = (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      e.preventDefault();
      closeEventModuleOverlay();
    }
  };
  document.addEventListener('keydown', overlayKeyHandler);

  detachOverlayListeners = () => {
    if (trigger) trigger.removeEventListener('click', onTriggerClick);
    if (menuBtn) menuBtn.removeEventListener('click', onMenuClick);
    closeBtn.removeEventListener('click', onCloseClick);
    backdrop.removeEventListener('click', onBackdropClick);
    if (overlayKeyHandler) {
      document.removeEventListener('keydown', overlayKeyHandler);
      overlayKeyHandler = null;
    }
    carouselCleanups.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        /* ignore */
      }
    });
    closeOverlayShell(overlay, trigger);
  };
}
